import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import EnrollmentModal from "./EnrollmentModal";

interface TrainingProgram {
  title: string;
  duration: string;
  startDate: string;
  price: string;
  description: string;
  topics: string[];
  image: string;
  category: string;
}

const NewTrainings: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState("");

  const trainingPrograms: TrainingProgram[] = [
    {
      title: "Industrial Welding Fundamentals",
      duration: "3 Months",
      startDate: "June 1, 2024",
      price: "GH₵ 2,500",
      description:
        "Comprehensive welding training covering MIG, TIG, and Arc welding techniques for industrial applications.",
      topics: [
        "Safety Procedures",
        "Welding Equipment Operation",
        "Joint Preparation",
        "Welding Techniques",
        "Quality Control",
        "Certification Preparation",
      ],
      image: "/foundations.png",
      category: "Welding",
    },
    {
      title: "Metal Fabrication Essentials",
      duration: "2 Months",
      startDate: "July 15, 2024",
      price: "GH₵ 2,000",
      description:
        "Learn metal fabrication techniques, blueprint reading, and project planning for industrial applications.",
      topics: [
        "Metal Properties",
        "Cutting Techniques",
        "Forming Methods",
        "Assembly Procedures",
        "Project Planning",
        "Safety Standards",
      ],
      image: "/fabrication.png",
      category: "Fabrication",
    },
    {
      title: "Industrial Equipment Maintenance",
      duration: "6 Weeks",
      startDate: "August 1, 2024",
      price: "GH₵ 1,800",
      description:
        "Master the skills needed for industrial equipment maintenance and troubleshooting.",
      topics: [
        "Preventive Maintenance",
        "Troubleshooting",
        "Equipment Repair",
        "Documentation",
        "Safety Protocols",
        "Tool Usage",
      ],
      image: "/maintainance.png",
      category: "Maintenance",
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Training Programs"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">
            Professional Training Programs
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Enhance your skills with our industry-focused training programs.
            Learn from experienced professionals and gain practical experience.
          </p>
        </div>
      </section>

      {/* Training Programs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trainingPrograms.map((program, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold">{program.title}</h3>
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                      {program.category}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{program.description}</p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Duration: {program.duration}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Starts: {program.startDate}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Fee: {program.price}
                    </div>
                  </div>
                  <div className="mb-6">
                    <h4 className="font-semibold mb-2">Topics Covered:</h4>
                    <ul className="space-y-1">
                      {program.topics.map((topic, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-gray-600"
                        >
                          <svg
                            className="w-4 h-4 text-orange-500 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          {topic}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedProgram(program.title);
                      setIsModalOpen(true);
                    }}
                    className="w-full bg-orange-500 text-white py-2 rounded hover:bg-orange-600 transition-colors"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Take the first step towards your career in industrial skills.
            Contact us to learn more about our training programs and enrollment
            process.
          </p>
          <button
            onClick={() => navigate("/contacts")}
            className="bg-white text-orange-500 px-8 py-3 rounded hover:bg-gray-100 transition-colors"
          >
            Contact Us
          </button>
        </div>
      </section>

      {/* Add the EnrollmentModal component */}
      <EnrollmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        programTitle={selectedProgram}
      />
    </div>
  );
};

export default NewTrainings;
